import { useCallback, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { useTheme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import {
  CheckCheck,
  FactoryIcon,
  GlobeIcon,
  LayoutGrid,
  LayoutList,
  RefreshCcw,
  RouterIcon,
  Wifi,
  X,
} from 'lucide-react';

import { Button } from '@/components/Button';
import Card from '@/components/Card';
import TableComponent, { type Column } from '@/components/TableComponent';
import api from '@/services/api';
import GenericDeviceCard, {
  type CardInfoField,
} from '@/components/GenericDeviceCard';
import CardPagination from '@/components/CardPagination';
import { BarProgress } from '@/pages/Dashboard/DeviceStatusCard/styles';
import WifiIcon from '../../../components/WifiIcon';
import { getRssiColor, getRssiIcon } from '@/utils/rssi';
import {
  LoadingBox,
  TopBarContainer,
  TitleActionsWrapper,
  FilterActionsWrapper,
  StyledToggleButtonGroup,
  ButtonContentStack,
} from './styles';

interface HostType {
  id?: string | number;
  hostName: string;
  macAddress: string;
  ipAddress: string;
  interfaceType?: string;
  networkName?: string;
  active?: boolean;
  rssi?: number;
  rssiClassification: string;
}

const COLOR_BY_TYPE: Record<string, 'primary' | 'secondary' | 'default'> = {
  'Wi-Fi': 'primary',
  Ethernet: 'secondary',
  Desconectado: 'default',
};

const getChipColor = (type: string) => {
  return COLOR_BY_TYPE[type] || 'default';
};

interface HostsProps {
  currentTab: string;
  equipmentId: number;
}

/** Connected hosts component */
/** Displays the connected hosts and hosts infos */
function Hosts({ currentTab, equipmentId }: HostsProps) {
  const theme = useTheme();
  const { t } = useTranslation();

  const [loading, setLoading] = useState<boolean>(false);
  const [devices, setDevices] = useState<HostType[]>([]);
  const [displayFormat, setDisplayFormat] = useState<string>('list');
  const [page, setPage] = useState<number>(0);

  const colorByRssiClassification = useMemo<Record<string, string>>(
    () => ({
      Excellent: theme.palette.status.online,
      Great: theme.palette.status.warning,
      Regular: theme.palette.status.warning,
      Bad: theme.palette.status.offline,
      Critical: theme.palette.status.offline,
    }),
    [theme]
  );

  const fetchHosts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get<HostType[]>(
        `/devices/${equipmentId}/hosts`
      );

      if (response.status === 200) {
        const responseData = response?.data || [];
        const orderedByType = [...responseData].sort((a, b) => {
          if (a && b && a.active === b.active) {
            return a.interfaceType?.localeCompare(b.interfaceType || '') || 0;
          }
          return a.active ? -1 : 1;
        });
        setDevices(orderedByType);
      } else {
        throw new Error(String(response.status));
      }
    } catch (error) {
      enqueueSnackbar(t('errors.fetchFailed'), { variant: 'error' });
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [equipmentId, t]);

  useEffect(() => {
    if (currentTab === 'connected-devices') {
      fetchHosts();
    }
  }, [currentTab, fetchHosts]);

  const columns: Column<HostType>[] = useMemo(
    () => [
      {
        id: 'number',
        label: t('equipment.connectedHosts.number'),
        render: (_: HostType[keyof HostType], row: HostType) => {
          return indexedDevices.get(row.macAddress);
        },
      },
      { id: 'hostName', label: t('equipment.connectedHosts.name') },
      {
        id: 'interfaceType',
        label: t('equipment.connectedHosts.type'),
        render: (_, row) => {
          if (typeof row === 'object' && row !== null)
            return (
              <Chip
                label={row.interfaceType}
                color={getChipColor(row.interfaceType || '')}
                size="small"
                sx={{ borderRadius: '4px' }}
              />
            );
        },
      },
      { id: 'ipAddress', label: t('equipment.connectedHosts.ipv4') },
      { id: 'macAddress', label: t('equipment.connectedHosts.mac') },
      {
        id: 'active',
        label: t('equipment.connectedHosts.active'),
        render: (_, row) => {
          if (typeof row === 'object' && row !== null)
            return row.active ? (
              <CheckCheck color={theme.palette.primary['01']} />
            ) : (
              <X />
            );
        },
      },
      {
        id: 'rssi',
        label: t('equipment.connectedHosts.signalLevel'),
        render: (_, row) => {
          if (row === null) return <div />;
          if (typeof row === 'object')
            return (
              <Stack direction="row" alignItems="center" gap="8px">
                <BarProgress
                  width="100px"
                  height="8px"
                  variant="determinate"
                  value={Math.max(
                    0,
                    Math.min(100, (Number(row.rssi) + 100) * 2)
                  )}
                  color={
                    colorByRssiClassification[row.rssiClassification || ''] ||
                    theme.palette.grey['05']
                  }
                />
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color={
                    colorByRssiClassification[row.rssiClassification || ''] ||
                    theme.palette.grey['05']
                  }
                >
                  {row.rssi}
                </Typography>
              </Stack>
            );
        },
      },
    ],
    [t, colorByRssiClassification, theme.palette.primary]
  );

  const indexedDevices = useMemo(() => {
    return new Map(devices.map((d, index) => [d.macAddress, index + 1]));
  }, [devices]);

  return (
    <Stack width="100%" gap="16px">
      <TopBarContainer>
        <TitleActionsWrapper>
          <Typography variant="h4" width="fit-content">
            {t('equipment.connectedHosts.title')}
          </Typography>
          <Button
            variant="primary"
            height="32px"
            onClick={fetchHosts}
            disabled={loading}
          >
            {loading ? (
              <ButtonContentStack>
                <CircularProgress
                  enableTrackSlot
                  size="18px"
                  sx={{ color: theme.palette.white['06'] }}
                />
                {t('equipment.connectedHosts.loadingButton')}
              </ButtonContentStack>
            ) : (
              <ButtonContentStack>
                <RefreshCcw size="18px" />
                {t('equipment.connectedHosts.refresh')}
              </ButtonContentStack>
            )}
          </Button>
        </TitleActionsWrapper>

        <FilterActionsWrapper>
          <StyledToggleButtonGroup
            color="primary"
            value={displayFormat}
            exclusive
            onChange={(
              _: React.MouseEvent<HTMLElement>,
              value: string | null
            ) => value && setDisplayFormat(value)}
            aria-label="displayFormat"
          >
            <ToggleButton value="list" sx={{ whiteSpace: 'nowrap' }}>
              <LayoutList size={18} style={{ marginRight: '4px' }} />
              {t('equipment.connectedHosts.displayList')}
            </ToggleButton>
            <ToggleButton value="card" sx={{ whiteSpace: 'nowrap' }}>
              <LayoutGrid size={18} style={{ marginRight: '4px' }} />
              {t('equipment.connectedHosts.displayCard')}
            </ToggleButton>
          </StyledToggleButtonGroup>
        </FilterActionsWrapper>
      </TopBarContainer>

      {loading && devices.length === 0 ? (
        <LoadingBox>
          <CircularProgress
            enableTrackSlot
            size="18px"
            sx={{ color: theme.palette.white['06'] }}
          />
          <Typography variant="caption" color={theme.palette.white['06']}>
            {t('equipment.connectedHosts.loading')}
          </Typography>
        </LoadingBox>
      ) : displayFormat === 'list' ? (
        <Card>
          <TableComponent<HostType>
            data={devices}
            columns={columns}
            page={page}
            setPage={setPage}
          />
          <TableContainer sx={{ width: '100%' }}>
            <Table sx={{ width: '100%' }}>
              <TableBody>
                {!loading && devices.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      {t('equipment.connectedHosts.noDevices')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      ) : (
        <CardPagination
          data={devices}
          gap="24px"
          cardWidth={324}
          cardGap={24}
          renderItem={(device) => {
            const rssiColor = getRssiColor(device.rssiClassification, theme);

            const fields: CardInfoField[] = [
              {
                key: 'hostName',
                label: t('equipment.connectedHosts.hostname'),
                value: device.hostName,
                icon: <RouterIcon size={16} />,
              },
              {
                key: 'macAddress',
                label: t('equipment.connectedHosts.macAddress'),
                value: device.macAddress,
                icon: <FactoryIcon size={16} />,
              },
              {
                key: 'ipAddress',
                label: t('map.card.ip'),
                value: device.ipAddress,
                icon: <GlobeIcon size={16} />,
              },
              {
                key: 'networkName',
                label: t('equipment.connectedHosts.name'),
                value: device.networkName,
                icon: <RouterIcon size={16} />,
              },
              {
                key: 'rssi',
                label: t('equipment.connectedHosts.signalLevel'),
                value:
                  device.rssi !== undefined && device.rssi !== null ? (
                    <Stack direction="row" alignItems="center" gap="8px">
                      <BarProgress
                        width="100px"
                        height="8px"
                        variant="determinate"
                        value={
                          typeof device.rssi === 'number'
                            ? Math.max(
                                0,
                                Math.min(100, (device.rssi + 100) * 2)
                              )
                            : 0
                        }
                        color={
                          rssiColor as
                            | 'primary'
                            | 'secondary'
                            | 'error'
                            | 'warning'
                            | 'info'
                            | 'success'
                            | undefined
                        }
                      />
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        color={rssiColor}
                      >
                        {device.rssi}
                      </Typography>
                    </Stack>
                  ) : null,
                icon: (
                  <WifiIcon
                    color={rssiColor || ''}
                    rssi={device.rssi || -1000}
                  />
                ),
              },
              {
                key: 'rssiClassification',
                label: t('equipment.connectedHosts.classification'),
                value: device.rssiClassification ? (
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color={rssiColor}
                  >
                    {device.rssiClassification}
                  </Typography>
                ) : null,
                icon: getRssiIcon(device.rssiClassification, theme),
              },
            ];

            return (
              <GenericDeviceCard
                key={device.macAddress}
                width="300px"
                chips={[
                  {
                    label: device.active
                      ? t('equipment.connectedHosts.connected')
                      : t('equipment.connectedHosts.disconnected'),
                    icon: device.active ? (
                      <CheckCheck size={16} color={theme.palette.white['01']} />
                    ) : (
                      <X size={16} color={theme.palette.white['01']} />
                    ),
                    bgColor: device.active
                      ? theme.palette.secondary.main
                      : theme.palette.grey['02'],
                    textColor: device.active
                      ? theme.palette.white['01']
                      : theme.palette.grey['03'],
                  },
                  {
                    label:
                      device.interfaceType === 'Wi-Fi'
                        ? t('equipment.connectedHosts.wifiInterface')
                        : device.interfaceType === 'Ethernet'
                          ? t('equipment.connectedHosts.ethernetInterface')
                          : device.interfaceType || '',
                    icon: (
                      <Wifi
                        size={16}
                        color={
                          device.active
                            ? theme.palette.white['06']
                            : theme.palette.grey['06']
                        }
                      />
                    ),
                    bgColor:
                      device.interfaceType === 'Wi-Fi'
                        ? theme.palette.primary.main
                        : device.interfaceType === 'Ethernet'
                          ? theme.palette.secondary.main
                          : theme.palette.grey['02'],
                    textColor:
                      device.interfaceType === 'Wi-Fi'
                        ? theme.palette.text.primary
                        : theme.palette.grey['03'],
                  },
                ]}
                fields={fields}
              />
            );
          }}
        />
      )}
    </Stack>
  );
}

export default Hosts;
